/**
 * 公共校验 Hook：与 AndInput 一致的校验逻辑（validateStore + propsStore），供所有支持校验的 And* 组件复用
 * 同时监听 validateAction（updateItem(sessionId_dataSet_validateAction, { validate: ts })），触发时执行校验。
 */
import { useCallback, useEffect, useMemo } from "react";
import { useAndStore } from "./store/useAndStore";
import type { AndStore } from "./store/useAndStore";
import { getDataSetStoreKey, getValidateStoreKey } from "./storeKeyUtils";
import { processValidationRules, validateWithSchema } from "./validationUtils";

export interface UseFieldValidationOptions {
  rules?: Array<{ type?: string; required?: boolean; message?: string; pattern?: string | RegExp; min?: number; max?: number }>;
  dataSet?: string;
  dataSetField?: string;
  sessionId?: string;
  id?: string;
  /** 是否设计器模式（设计器下不展示 help/validateStatus） */
  develop?: boolean;
}

/**
 * 返回校验方法及当前校验状态，与 AndInput 行为一致：
 * - 有 dataSet+dataSetField 时 help 来自 validateStore，否则来自 propsStore
 * - validate(value) 使用 async-validator，结果写入 validateStore 与 propsStore
 */
export function useFieldValidation(options: UseFieldValidationOptions) {
  const { rules, dataSet, dataSetField, sessionId, id, develop } = options;

  const setValidateResult = useAndStore((s: AndStore) => s.setValidateResult);
  const getProps = useAndStore((s: AndStore) => s.getProps);
  const setProps = useAndStore((s: AndStore) => s.setProps);

  const validateStoreKey = useMemo(
    () => getValidateStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );
  const validateErrorMsg = useAndStore((s: AndStore) =>
    validateStoreKey && dataSetField
      ? (s.validateStore[validateStoreKey]?.[dataSetField] ?? "")
      : ""
  );

  const storeProps = useAndStore((s: AndStore) =>
    sessionId && id ? s.propsStore[`${sessionId}_props`]?.[id] ?? null : null
  );

  const getDataSet = useAndStore((s: AndStore) => s.getDataSet);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );
  const validateActionKey = useMemo(
    () => (dataSet ? getDataSetStoreKey(sessionId, `${dataSet}_validateAction`) : undefined),
    [sessionId, dataSet]
  );
  const validateActionTrigger = useAndStore((s: AndStore) =>
    validateActionKey
      ? (s.dataSets[validateActionKey] as { validate?: number } | undefined)?.validate
      : undefined
  );

  const applyResult = useCallback(
    (help: string) => {
      const msg = help ?? "";
      if (sessionId && dataSet && dataSetField)
        setValidateResult(sessionId, dataSet, dataSetField, msg);
      if (sessionId && id) {
        const current = getProps(sessionId, id) || {};
        setProps(sessionId, id, { ...current, validateStatus: msg ? "error" : undefined, help: msg });
      }
    },
    [sessionId, dataSet, dataSetField, id, setValidateResult, getProps, setProps]
  );

  const validate = useCallback(
    (value: unknown) => {
      const processedRules = processValidationRules(rules || []);
      validateWithSchema(
        processedRules,
        value,
        () => applyResult(""),
        (errs) => applyResult(errs[0]?.message ?? "")
      );
    },
    [rules, applyResult]
  );

  const help = !develop
    ? (sessionId && dataSet && dataSetField ? validateErrorMsg : (storeProps?.help as string) ?? "")
    : undefined;
  const validateStatus = !develop && help ? "error" : undefined;

  useEffect(() => {
    if (validateActionTrigger == null || !dataSetKey || !dataSetField) return;
    const value = getDataSet(dataSetKey, dataSetField);
    validate(value);
  }, [validateActionTrigger, dataSetKey, dataSetField, getDataSet, validate]);

  return { validate, help, validateStatus };
}
